import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Accordion from 'react-bootstrap/Accordion';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Spinner from 'react-bootstrap/Spinner';
import axios from 'axios';
import number_3 from '../../images/number-3.png';
import number_4 from '../../images/number-4.png';
import connected_icon from '../../images/correct.png';
import { showToast as toast } from '../../toastManager';

// Settings → Integrations: Google Drive, Gmail and Slack. These are
// ACCOUNT-level connections (one per store, like Google Ads); WHICH files,
// emails and channels each workspace may use is chosen per workspace on
// Workspace Settings → Preferences. Server: /api/integrations
// (routes/api/integrations/integrations_routes.js). Drive and Gmail reuse the
// Google Ads OAuth client with incremental consent, so Google asks only for the
// one new read-only permission.
//
// The "(C)" tiles (2026-10-06) connect the same apps — plus Shopify and Google
// Analytics — through Composio. While Google and Slack review our own apps,
// our three tiles show only where that app is already connected
// (`native.visible` from /status); where both are connected, ours is used.
//
// Rendered inside Home.jsx's <Accordion>, continuing its numbered steps.

const PREFERENCES_PATH = '/cognitive-flows/setup';

const signInBtn = {
    backgroundColor: 'var(--cux-btn, rgb(19, 40, 72))', color: 'white', minWidth: '8rem', border: 'none',
    padding: '2px 12px 2px 2px', cursor: 'pointer', display: 'flex', alignItems: 'center', marginLeft: 'auto',
};
const logoChip = { background: 'white', padding: '6px', marginRight: '10px', flexShrink: 0 };
const hint = { fontSize: '12.5px', color: 'var(--text-secondary, #64748b)', marginTop: '4px' };

// number-5 has no image asset; drawn to match the numbered PNGs (black disc,
// white heavy numeral) at the same 15px size.
function StepNumber({ n }) {
    if (n === 3) return <img src={number_3} alt="3" width="15px" height="15px" />;
    if (n === 4) return <img src={number_4} alt="4" width="15px" height="15px" />;
    return (
        <span aria-hidden="true" style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15,
            borderRadius: '50%', background: '#000', color: '#fff', fontSize: '9.5px', fontWeight: 800, lineHeight: 1,
        }}>{n}</span>
    );
}

const DriveLogo = () => (
    <svg style={logoChip} width="30px" height="30px" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Google Drive">
        <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
        <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
        <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.5l5.85 11.5z" fill="#ea4335" />
        <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
        <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
        <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
);

const GmailLogo = () => (
    <svg style={logoChip} width="30px" height="30px" viewBox="52 42 88 66" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Gmail">
        <path fill="#4285f4" d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6" />
        <path fill="#34a853" d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15" />
        <path fill="#fbbc04" d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2" />
        <path fill="#ea4335" d="M72 74V48l24 18 24-18v26L96 92" />
        <path fill="#c5221f" d="M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2" />
    </svg>
);

const SlackLogo = () => (
    <svg style={logoChip} width="30px" height="30px" viewBox="0 0 122.8 122.8" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Slack">
        <path d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" fill="#e01e5a" />
        <path d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" fill="#36c5f0" />
        <path d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" fill="#2eb67d" />
        <path d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" fill="#ecb22e" />
    </svg>
);

const ShopifyLogo = () => (
    <svg style={logoChip} width="30px" height="30px" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Shopify">
        <path d="M11 16h26l-2.3 25a3 3 0 0 1-3 2.7H16.3a3 3 0 0 1-3-2.7L11 16z" fill="#95BF47" />
        <path d="M17.5 19v-5a6.5 6.5 0 0 1 13 0v5" stroke="#5E8E3E" strokeWidth="3" fill="none" strokeLinecap="round" />
    </svg>
);

const AnalyticsLogo = () => (
    <svg style={logoChip} width="30px" height="30px" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Google Analytics">
        <rect x="30" y="6" width="10" height="36" rx="5" fill="#F9AB00" />
        <rect x="19" y="18" width="10" height="24" rx="5" fill="#E37400" />
        <circle cx="13" cy="37" r="5" fill="#E37400" />
    </svg>
);

// A Composio "(C)" tile: same shape as ours, connection state from /status's
// `composio.apps.<app>`.
const composioItem = ({ app, name, Logo, pitch, connectedHint }) => ({
    key: `c-${app}`,
    composio: true,
    title: `Integrate ${name} (C)`,
    name: `${name} (C)`,
    Logo,
    connect: `/api/integrations/composio/${app}/connect`,
    disconnect: `/api/integrations/composio/${app}/disconnect`,
    pitch,
    detail: 'Sign-in runs through our connection partner, Composio.',
    read: (s) => ({ configured: !!s?.composio?.configured, ...(s?.composio?.apps?.[app] || {}) }),
    connectedText: (st) => <>{name} (C) connected{st.label ? <> to <b>{st.label}</b></> : null}.</>,
    connectedHint,
});

const ITEMS = [
    {
        key: 'drive',
        native: 'drive',
        title: 'Integrate Google Drive',
        name: 'Google Drive',
        Logo: DriveLogo,
        connect: '/api/integrations/google/drive/connect',
        disconnect: '/api/integrations/google/drive/disconnect',
        pitch: 'Give your agents read-only access to Google Drive files.',
        detail: 'Google asks only for Drive read access — your Google Ads connection is untouched.',
        read: (s) => ({ configured: !!s?.google?.configured, ...(s?.google?.drive || {}) }),
        connectedText: (st) => <>Google Drive connected{st.email ? <> as <b>{st.email}</b></> : null}.</>,
    },
    {
        key: 'gmail',
        native: 'gmail',
        title: 'Integrate Gmail',
        name: 'Gmail',
        Logo: GmailLogo,
        connect: '/api/integrations/google/gmail/connect',
        disconnect: '/api/integrations/google/gmail/disconnect',
        pitch: 'Give your agents read-only access to Gmail messages.',
        detail: 'Google asks only for Gmail read access — nothing is ever sent or deleted.',
        read: (s) => ({ configured: !!s?.google?.configured, ...(s?.google?.gmail || {}) }),
        connectedText: (st) => <>Gmail connected{st.email ? <> as <b>{st.email}</b></> : null}.</>,
    },
    {
        key: 'slack',
        native: 'slack',
        title: 'Integrate Slack',
        name: 'Slack',
        Logo: SlackLogo,
        connect: '/api/integrations/slack/connect',
        disconnect: '/api/integrations/slack/disconnect',
        pitch: 'Let your agents read Slack channels and post updates to them.',
        detail: 'Installs a bot in your Slack workspace. Private channels need the bot invited to them.',
        read: (s) => ({ ...(s?.slack || {}) }),
        connectedText: (st) => <>Slack connected{st.teamName ? <> to <b>{st.teamName}</b></> : null}.</>,
    },
    composioItem({ app: 'gmail', name: 'Gmail', Logo: GmailLogo, pitch: 'Give your agents read-only access to Gmail messages.' }),
    composioItem({ app: 'drive', name: 'Google Drive', Logo: DriveLogo, pitch: 'Give your agents read-only access to Google Drive files.' }),
    composioItem({ app: 'slack', name: 'Slack', Logo: SlackLogo, pitch: 'Let your agents read Slack channels.' }),
    composioItem({
        app: 'shopify', name: 'Shopify', Logo: ShopifyLogo,
        pitch: 'Bring Shopify orders, products, customers and daily sales into Data Sources, agents and the daily BigQuery sync.',
        connectedHint: 'Add the Shopify reports to a workspace in',
    }),
    composioItem({
        app: 'google_analytics', name: 'Google Analytics', Logo: AnalyticsLogo,
        pitch: 'Bring Google Analytics 4 reports into Data Sources, agents and the daily BigQuery sync.',
        connectedHint: 'Pick each workspace\'s GA4 property in',
    }),
];

export const APP_INTEGRATION_EVENT_KEYS = ITEMS.map((i) => `app-${i.key}`);

export default function AppIntegrationItems({ app_url = '', onPopupOpen }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({});
    const timers = useRef([]);

    const fetchStatus = useCallback(() => {
        return axios.get(`${app_url}/api/integrations/status`, { withCredentials: true })
            .then(({ data }) => { if (data?.success) setStatus(data); })
            .catch(() => { /* tiles fall back to "not connected" */ })
            .finally(() => setLoading(false));
    }, [app_url]);

    useEffect(() => {
        fetchStatus();
        const onAuthChanged = (e) => { if (e.key === 'scalex_auth_changed') fetchStatus(); };
        window.addEventListener('storage', onAuthChanged);
        const pending = timers.current;
        return () => {
            window.removeEventListener('storage', onAuthChanged);
            pending.forEach(clearInterval);
        };
    }, [fetchStatus]);

    const setItemBusy = (key, value) => setBusy((b) => ({ ...b, [key]: value }));

    const connect = (item) => {
        // Open the popup synchronously (inside the click gesture) so blockers
        // allow it, then point it at the provider once the server mints the
        // URL. The callback page closes itself; polling popup.closed refreshes.
        const popup = window.open('about:blank', 'oauth_popup', 'width=600,height=720,left=200,top=100');
        if (!popup) {
            toast.error(`Please allow popups to connect ${item.name}.`);
            return;
        }
        setItemBusy(item.key, true);
        axios.post(`${app_url}${item.connect}`, {}, { withCredentials: true })
            .then(({ data }) => {
                popup.location.href = data.data.redirect_url;
                if (onPopupOpen) onPopupOpen(popup);
                const timer = setInterval(() => {
                    if (popup.closed) {
                        clearInterval(timer);
                        fetchStatus().finally(() => setItemBusy(item.key, false));
                    }
                }, 600);
                timers.current.push(timer);
            })
            .catch((error) => {
                popup.close();
                setItemBusy(item.key, false);
                toast.error(error?.response?.data?.message || 'Please try again.');
            });
    };

    const disconnect = async (item) => {
        setItemBusy(item.key, true);
        try {
            await axios.post(`${app_url}${item.disconnect}`, {}, { withCredentials: true });
            toast.success(`${item.name} disconnected.`);
            try { localStorage.setItem('scalex_auth_changed', Date.now().toString()); } catch { /* private mode */ }
            await fetchStatus();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Please try again.');
        } finally {
            setItemBusy(item.key, false);
        }
    };

    // Our own three show only where /status says so; the (C) tiles always do.
    // Until /status answers, ours stay hidden so a store that only has (C)
    // never sees them flash.
    const visible = ITEMS.filter((item) => !item.native || status?.native?.visible?.[item.native] === true);

    return visible.map((item, index) => {
        const step = 3 + index;
        const st = item.read(status);
        const isBusy = loading || busy[item.key];
        return (
            <Accordion.Item eventKey={`app-${item.key}`} key={item.key} data-testid={`integrations-${item.key}`}>
                <Accordion.Header className='google-ads-accordion-button'>
                    <StepNumber n={step} />
                    <b style={{ marginLeft: '10px' }}>{item.title}</b>
                </Accordion.Header>
                <Accordion.Body className='pt-2'>
                    <Container className='google_and_meta_integration_accordion_container'>
                        {isBusy ? (
                            <Row className='mt-3'>
                                <Col className='d-flex align-items-center justify-content-center'>
                                    <Spinner animation="border" size="sm" />
                                </Col>
                            </Row>
                        ) : st.connected ? (
                            <Row className='mt-3'>
                                <Col className='d-flex align-items-start' sm={8}>
                                    <img alt='connected' src={connected_icon} width="22px" style={{ marginRight: '10px', marginTop: '1px' }} />
                                    <div>
                                        <span>{item.connectedText(st)}</span>
                                        <div style={hint}>
                                            {item.connectedHint || 'Choose what each workspace uses in'}{' '}
                                            <Link to={PREFERENCES_PATH} style={{ color: 'var(--cux-accent, #2563eb)' }}>
                                                Workspace Settings → Preferences
                                            </Link>.
                                        </div>
                                    </div>
                                </Col>
                                <Col sm={4} className="google_and_meta_integration_status_col d-flex align-items-center justify-content-end">
                                    <button className='back-button btn-height' onClick={() => disconnect(item)}>
                                        <span style={{ margin: 'auto', whiteSpace: 'nowrap' }}>Sign Out</span>
                                    </button>
                                </Col>
                            </Row>
                        ) : (
                            <Row className='mt-3'>
                                <Col className='d-flex flex-column justify-content-center' sm={8}>
                                    <span>{item.pitch}</span>
                                    <span style={hint}>
                                        {st.configured ? item.detail : `${item.name} isn't set up on this server yet — contact support to enable it.`}
                                    </span>
                                </Col>
                                <Col sm={4} className='google_and_meta_integration_status_col d-flex align-items-center justify-content-end'>
                                    <button
                                        style={{ ...signInBtn, opacity: st.configured ? 1 : 0.5, cursor: st.configured ? 'pointer' : 'not-allowed' }}
                                        disabled={!st.configured}
                                        onClick={() => connect(item)}
                                        aria-label={`Sign in to ${item.name}`}
                                    >
                                        <item.Logo />
                                        <h6 style={{ margin: 'auto' }}>Sign In</h6>
                                    </button>
                                </Col>
                            </Row>
                        )}
                    </Container>
                </Accordion.Body>
            </Accordion.Item>
        );
    });
}
